import { useEffect, useState, useCallback } from 'react';
import api from '../../../../../../api/api';
import useEvents from './useEvents';
import productsReducer from '../reducers/productsReducer';
import selectedProductReducer from '../reducers/selectedProductReducer';

export default function useProducts(shop, options = {}) {
    if (!shop) return null
    options = {
        ...options,
        pagination: Object.assign({ page: 1, perPage: 10 }, options.pagination || {}),
    };

    const { emit } = useEvents();
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 });
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [selectedProduct, setSelectedProduct] = useState();

    useEffect(async () => {
        setIsLoading(true);
        try {
            const list = await api.getProductList(shop.id, options.pagination.page, options.pagination.perPage);
            const meta = { currentPage: list.meta.currentPage, totalPages: list.meta.lastPage };
            setProducts(list.data);
            setPagination(meta);
            if (!list.data.length) {
                emit('shop.products.empty', null, meta);
            }
        } catch (err) {
            emit('error', err);
        }
        setIsLoading(false);
    }, [shop.id, options.pagination.page, options.pagination.perPage, emit, refreshCounter]);

    const dispatch = useCallback((action) => {
        setSelectedProduct((state) => {
            const newState = selectedProductReducer(state, action);
            if (newState && state !== newState) {
                emit('shop.products.updated', newState, { changes: action.payload?.changes });
            }
            return newState;
        });
        setProducts((state) => productsReducer(state, action));
    }, [emit]);

    const refresh = useCallback(() => setRefreshCounter((value) => value + 1), []);

    const addProduct = useCallback(async (attrs = { title: 'New Product', price: '1' }) => {
        let product;
        setIsLoading(true);
        try {
            product = await api.createProduct(shop.id, attrs);
            emit('shop.products.outdated');
        } catch (err) {
            emit('error', err);
        }
        setIsLoading(false);
        return product;
    }, [shop.id, emit]);

    const deleteProduct = useCallback(async (product) => {
        setIsLoading(true);
        try {
            await api.deleteProduct(shop.id, product.id);
            dispatch({ type: 'deleted', payload: { product } });
            emit('shop.products.outdated');
        } catch (err) {
            emit('error', err);
        }
        setIsLoading(false);
    }, [shop.id, emit]);

    const updateProduct = useCallback(async (product, { price, ...changes } = {}) => {
        setIsLoading(true);
        try {
            if (Object.keys(changes).length > 0) {
                await api.updateProduct(shop.id, changes, product.id);
                dispatch({ type: 'updated', payload: { product, changes } });
            }
            if (price) {
                const variant = await api.updateProductPrice(shop.id, { price }, product);
                dispatch({ type: 'variantUpdated', payload: { product, variant, changes: { price } } });
            }
        } catch (err) {
            emit('error', err);
        }
        setIsLoading(false);
    }, [shop.id, dispatch, emit]);

    const addImageToProduct = useCallback(async (product, imageFile) => {
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('image', imageFile);
            const image = await api.addImageToProduct(shop.id, product.id, formData);
            dispatch({ type: 'imageAdded', payload: { product, image } });
        } catch (err) {
            emit('error', err);
        }
        setIsLoading(false);
    }, [shop.id, dispatch, emit]);

    const updateProductImage = useCallback(async (product, image, imageFile) => {
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('image', imageFile);
            const updatedImage = await api.changeImageInProduct(shop.id, product.id, formData, image.id);
            dispatch({ type: 'imageUpdated', payload: { product, image: updatedImage } });
        } catch (err) {
            emit('error', err);
        }
        setIsLoading(false);
    }, [shop.id, emit, dispatch]);

    const updateProductDefaultImage = useCallback((product, imageFile) => {
        if (product.images.length) {
            return updateProductImage(product, product.images[0], imageFile);
        }
        return addImageToProduct(product, imageFile);
    }, [updateProductImage, addImageToProduct]);

    const deleteImageFromProduct = useCallback(async (product, image) => {
        setIsLoading(true);
        try {
            await api.removeImageFromProduct(shop.id, product.id, image.id);
            dispatch({ type: 'imageDeleted', payload: { product, image } });
        } catch (err) {
            emit('error', err);
        }
        setIsLoading(false);
    }, [shop.id, emit]);

    return {
        refresh,
        isLoading,
        pagination,
        add: addProduct,
        list: products,
        update: updateProduct,
        delete: deleteProduct,
        select: setSelectedProduct,
        selected: selectedProduct,
        addImage: addImageToProduct,
        updateImage: updateProductImage,
        deleteImage: deleteImageFromProduct,
        updateDefaultImage: updateProductDefaultImage,
    };
}